import { LIVE_MESSAGE_EVENT } from '../../components/chat/events';

import { isRecord } from '../core/record';
import type { ChannelId, SyncCursor, SyncEvent, SyncResult, WorkspaceId } from '../core/types';
import type { EchoDexieBuffer } from './dexieBuffer';
import {
  customEventDetail,
  defaultEventTarget,
  type EchoSocketEventTarget,
  rawLiveMessageRow,
  syncEventFromSocketDetail,
} from './socket';
import { parseEchoSocketMessageEventDetail } from './wire';

const CABLE_EVENT = 'output:internal:cable';
const DEFAULT_MAX_QUEUED_EVENTS = 200;

type FeedReplicatorBuffer = Pick<
  EchoDexieBuffer,
  'applySyncEvents' | 'markConversationStale' | 'membershipGeneration' | 'persistEvent' | 'purgeWorkspace' | 'readSyncCursor'
>;

/** Channel-scoped sync surface used to reconcile a durable cursor before live replication. */
export interface FeedReplicatorSyncApi {
  sync(cursor: SyncCursor): Promise<SyncResult>;
}

/** Dependencies for the page-lifetime feed replicator. */
export interface FeedReplicatorOptions {
  readonly buffer: FeedReplicatorBuffer;
  readonly createSyncApi: (channelId: ChannelId) => FeedReplicatorSyncApi;
  readonly eventTarget?: EchoSocketEventTarget;
  readonly getActiveChannelId: () => ChannelId | null;
  readonly maxQueuedEvents?: number;
  readonly resolveAccountId: (workspaceId: WorkspaceId) => Promise<string | undefined>;
}

type ChannelPhase = 'idle' | 'syncing' | 'live';

interface ChannelState {
  accountId?: string;
  chain: Promise<void>;
  generation: number;
  phase: ChannelPhase;
  queue: SyncEvent[];
  readonly workspaceId: WorkspaceId;
}

/** Replicates live message events for cached channels into the durable feed buffer, across workspaces. */
export class FeedReplicator {
  private readonly buffer: FeedReplicatorBuffer;
  private readonly createSyncApi: (channelId: ChannelId) => FeedReplicatorSyncApi;
  private readonly eventTarget: EchoSocketEventTarget;
  private readonly getActiveChannelId: () => ChannelId | null;
  private readonly maxQueuedEvents: number;
  private readonly resolveAccountId: (workspaceId: WorkspaceId) => Promise<string | undefined>;
  private readonly channels = new Map<ChannelId, ChannelState>();
  private readonly purgingWorkspaces = new Set<WorkspaceId>();
  private started = false;

  constructor(options: FeedReplicatorOptions) {
    this.buffer = options.buffer;
    this.createSyncApi = options.createSyncApi;
    this.eventTarget = options.eventTarget ?? defaultEventTarget();
    this.getActiveChannelId = options.getActiveChannelId;
    this.maxQueuedEvents = Math.max(1, Math.floor(options.maxQueuedEvents ?? DEFAULT_MAX_QUEUED_EVENTS));
    this.resolveAccountId = options.resolveAccountId;
  }

  start(): void {
    if (this.started) return;
    this.eventTarget.addEventListener(LIVE_MESSAGE_EVENT, this.handleMessageEvent);
    this.eventTarget.addEventListener(CABLE_EVENT, this.handleCableEvent);
    // Reaction state is repaired when the channel boots and reconciles.
    this.started = true;
  }

  stop(): void {
    if (this.started) {
      this.eventTarget.removeEventListener(LIVE_MESSAGE_EVENT, this.handleMessageEvent);
      this.eventTarget.removeEventListener(CABLE_EVENT, this.handleCableEvent);
    }
    this.started = false;
    this.resetAllChannels();
    this.channels.clear();
  }

  /** Resets a catchup-affected background channel so its next event must pass the sync-first gate. */
  resetChannelFromCatchup(workspaceId: WorkspaceId, channelId: ChannelId): void {
    const state = this.channels.get(channelId);
    if (!state || state.workspaceId !== workspaceId) return;
    this.resetChannel(state);
  }

  /** Resets every background channel so its next event must pass the sync-first gate. */
  resetAllChannelsForCatchup(): void {
    this.resetAllChannels();
  }

  /** Fences workspace replication, drains prior channel work, and purges its feed-owned state. */
  async purgeWorkspace(workspaceId: WorkspaceId): Promise<void> {
    this.purgingWorkspaces.add(workspaceId);
    try {
      const states = [...this.channels.entries()].filter(([, state]) => state.workspaceId === workspaceId);
      for (const [channelId, state] of states) {
        this.resetChannel(state);
        this.channels.delete(channelId);
      }
      await Promise.allSettled(states.map(([, state]) => state.chain));
      await this.buffer.purgeWorkspace(workspaceId);
    } finally {
      this.purgingWorkspaces.delete(workspaceId);
    }
  }

  private readonly handleMessageEvent: EventListener = (event) => {
    const detail = customEventDetail(event);
    // Raw-field rejects keep the full wire parse off the hot path: the active channel's
    // own socket adapter already parses its events, and pseudotype rows are out of
    // scope here.
    const row = rawLiveMessageRow(detail);
    if (!row || row.pseudotype) return;
    if (typeof row.channel_id !== 'string' || typeof row.workspace_id !== 'string') return;
    if (row.channel_id === this.getActiveChannelId()) {
      const state = this.channels.get(row.channel_id);
      if (state) this.resetChannel(state);
      return;
    }

    const parsed = parseEchoSocketMessageEventDetail(detail);
    if (!parsed.ok) return;
    this.acceptEvent(
      parsed.value.message.channelId,
      parsed.value.message.workspaceId,
      syncEventFromSocketDetail(parsed.value),
    );
  };

  private readonly handleCableEvent: EventListener = (event) => {
    const detail = customEventDetail(event);
    if (isRecord(detail) && detail.connected === false) this.resetAllChannels();
  };

  private acceptEvent(channelId: ChannelId, workspaceId: WorkspaceId, event: SyncEvent): void {
    if (this.purgingWorkspaces.has(workspaceId)) return;
    const state = this.channelState(channelId, workspaceId);
    if (state.phase === 'syncing') {
      state.queue.push(event);
      if (state.queue.length > this.maxQueuedEvents) this.resetChannel(state);
      return;
    }

    if (state.phase === 'live') {
      this.schedule(channelId, state, (generation) => this.persistIfCurrent(channelId, state, generation, event));
      return;
    }

    state.phase = 'syncing';
    state.queue = [event];
    state.generation += 1;
    this.schedule(channelId, state, (generation) => this.syncAndDrain(channelId, state, generation));
  }

  private async syncAndDrain(channelId: ChannelId, state: ChannelState, generation: number): Promise<void> {
    if (!this.isCurrentBackgroundChannel(channelId, state, generation)) return;
    const accountId = state.accountId ?? (await this.resolveAccountId(state.workspaceId));
    if (!this.isCurrentBackgroundChannel(channelId, state, generation)) return;
    if (!accountId) {
      this.resetChannelIfCurrent(channelId, state, generation);
      return;
    }
    state.accountId = accountId;

    const cursor = await this.buffer.readSyncCursor(accountId, channelId);
    if (!this.isCurrentBackgroundChannel(channelId, state, generation)) return;
    if (cursor === undefined || cursor === null) {
      this.resetChannelIfCurrent(channelId, state, generation);
      return;
    }

    const result = await this.createSyncApi(channelId).sync(cursor);
    if (!this.isCurrentBackgroundChannel(channelId, state, generation)) return;
    if (result.overflow) {
      await this.buffer.markConversationStale(accountId, channelId, this.buffer.membershipGeneration(accountId));
    } else {
      await this.buffer.applySyncEvents(accountId, channelId, result.events, result.nextCursor);
    }
    if (!this.isCurrentBackgroundChannel(channelId, state, generation)) return;

    state.phase = 'live';
    for (const queued of state.queue.splice(0)) {
      await this.persistIfCurrent(channelId, state, generation, queued);
    }
  }

  private async persistIfCurrent(
    channelId: ChannelId,
    state: ChannelState,
    generation: number,
    event: SyncEvent,
  ): Promise<void> {
    if (!this.isCurrentBackgroundChannel(channelId, state, generation)) return;
    const accountId = state.accountId;
    if (!accountId) {
      this.resetChannelIfCurrent(channelId, state, generation);
      return;
    }
    await this.buffer.persistEvent(accountId, channelId, event, this.buffer.membershipGeneration(accountId));
  }

  private schedule(
    channelId: ChannelId,
    state: ChannelState,
    task: (generation: number) => Promise<void>,
  ): void {
    const generation = state.generation;
    state.chain = state.chain
      .then(() => task(generation))
      .catch(() => this.resetChannelIfCurrent(channelId, state, generation));
  }

  private isCurrentBackgroundChannel(channelId: ChannelId, state: ChannelState, generation: number): boolean {
    if (!this.started || this.channels.get(channelId) !== state || state.generation !== generation) return false;
    if (this.getActiveChannelId() !== channelId) return true;
    this.resetChannel(state);
    return false;
  }

  private channelState(channelId: ChannelId, workspaceId: WorkspaceId): ChannelState {
    const existing = this.channels.get(channelId);
    if (existing) return existing;
    const state: ChannelState = {
      chain: Promise.resolve(),
      generation: 0,
      phase: 'idle',
      queue: [],
      workspaceId,
    };
    this.channels.set(channelId, state);
    return state;
  }

  private resetAllChannels(): void {
    for (const state of this.channels.values()) this.resetChannel(state);
  }

  private resetChannelIfCurrent(channelId: ChannelId, state: ChannelState, generation: number): void {
    if (this.channels.get(channelId) === state && state.generation === generation) this.resetChannel(state);
  }

  private resetChannel(state: ChannelState): void {
    // accountId survives resets: the channel→workspace→profile mapping is stable for a
    // page session, so the resolver runs at most once per channel per replicator.
    state.generation += 1;
    state.phase = 'idle';
    state.queue = [];
  }
}
